/**
 * MINI-JEU — ATTRAPE / ÉVITE (arcade). Un panier au doigt en bas ; des objets tombent :
 * attrape les bons (+1), évite les bombes (−1 vie). Vitesse croissante. Atteindre le
 * score cible = victoire → récompense (créature) via /api/child/minigame/reward.
 * Thémé par enfant (getChildId) : Isaac = espace/dino, Bao = kawaii.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { getChildId } from "./childSession";
import { createGameAudio, type GameAudio } from "./gameSounds";

const TARGET = 15; // 3 niveaux de 5 objets (progression Barkley : paliers atteignables)
type Drop = { emoji: string; name: string; rarity: string } | null;

function themeFor(cid: string | null) {
  if (cid === "bao") return { good: ["⭐", "💖", "🍬", "🌸", "🦄"], bad: "💣", basket: "🧺" };
  return { good: ["⭐", "☄️", "💎", "🦴", "🚀"], bad: "💣", basket: "🧺" };
}

type Obj = { x: number; y: number; vy: number; emoji: string; good: boolean; size: number };

export default function CatchGame({ apiBase, onClose }: { apiBase: string; onClose: () => void }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [score, setScore] = useState(0);
  const [lives, setLives] = useState(3);
  const [level, setLevel] = useState(1);
  const [status, setStatus] = useState<"play" | "won" | "lost">("play");
  const [reward, setReward] = useState<Drop>(null);

  // état mutable du jeu (hors React pour la boucle)
  const g = useRef({
    objs: [] as Obj[], basketX: 0.5, w: 0, h: 0, lastSpawn: 0, raf: 0,
    score: 0, lives: 3, level: 1, over: false, theme: themeFor(getChildId()),
  });
  const sfx = useRef<GameAudio | null>(null);
  if (!sfx.current) sfx.current = createGameAudio();
  useEffect(() => () => sfx.current?.dispose(), []);

  const finish = useCallback(async (won: boolean) => {
    g.current.over = true;
    cancelAnimationFrame(g.current.raf);
    setStatus(won ? "won" : "lost");
    sfx.current?.[won ? "win" : "lose"]();
    if (won) {
      try {
        const r = await fetch(`${apiBase}/api/child/minigame/reward`, { method: "POST" });
        const d = await r.json();
        if (d && d.drop) setReward(d.drop);
      } catch { /* pas de récompense hors-ligne */ }
    }
  }, [apiBase]);

  const start = useCallback(() => {
    const st = g.current;
    st.objs = []; st.basketX = 0.5; st.lastSpawn = 0; st.score = 0; st.lives = 3; st.level = 1;
    st.over = false; st.theme = themeFor(getChildId());
    setScore(0); setLives(3); setLevel(1); setReward(null); setStatus("play");
  }, []);

  useEffect(() => {
    const cvs = canvasRef.current;
    if (!cvs) return;
    const ctx = cvs.getContext("2d")!;
    const accent = (getComputedStyle(document.documentElement).getPropertyValue("--kid-cyan") || "#00d4ff").trim();
    const resize = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      g.current.w = cvs.clientWidth; g.current.h = cvs.clientHeight;
      cvs.width = g.current.w * dpr; cvs.height = g.current.h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    let last = performance.now();
    const loop = (t: number) => {
      const st = g.current;
      if (st.over) return;
      const dt = Math.min(50, t - last); last = t;
      const { w, h, theme } = st;
      // Progression PAR PALIER (Barkley) : 3 niveaux de 5 objets. Le niveau 1 est facile
      // et SANS bombe (on prend confiance) ; la vitesse monte doucement par niveau.
      const level = st.score < 5 ? 1 : st.score < 10 ? 2 : 3;
      if (level !== st.level) { st.level = level; setLevel(level); }
      const P = level === 1 ? { spawn: 1350, vy: 0.055, bomb: 0.0 }
        : level === 2 ? { spawn: 1100, vy: 0.075, bomb: 0.12 }
        : { spawn: 950, vy: 0.092, bomb: 0.18 };
      if (t - st.lastSpawn > P.spawn) {
        st.lastSpawn = t;
        const good = Math.random() > P.bomb;
        const emoji = good ? theme.good[(Math.random() * theme.good.length) | 0] : theme.bad;
        st.objs.push({ x: 0.1 + Math.random() * 0.8, y: -0.05, vy: P.vy * (0.85 + Math.random() * 0.35), emoji, good, size: 36 });
      }
      const basketY = 0.8, catchR = 0.17; // panier remonté (le doigt reste dessous) + zone large
      for (const o of st.objs) {
        o.y += (o.vy * dt) / 100;
        if (o.y >= basketY && o.y < basketY + 0.06 && Math.abs(o.x - st.basketX) < catchR) {
          o.y = 2; // consommé
          if (o.good) { st.score++; setScore(st.score); sfx.current?.point(); if (st.score >= TARGET) { finish(true); return; } }
          else { st.lives--; setLives(st.lives); sfx.current?.hit(); if (st.lives <= 0) { finish(false); return; } }
        }
      }
      st.objs = st.objs.filter((o) => o.y < 1.15);
      // rendu
      ctx.clearRect(0, 0, w, h);
      ctx.textAlign = "center"; ctx.textBaseline = "middle";
      for (const o of st.objs) {
        ctx.font = `${o.size}px system-ui`;
        ctx.fillText(o.emoji, o.x * w, o.y * h);
      }
      // halo de la zone d'attrape (sous le panier) — on voit bien où viser
      ctx.save();
      ctx.globalAlpha = 0.18;
      ctx.fillStyle = accent;
      ctx.beginPath();
      ctx.ellipse(st.basketX * w, basketY * h + 20, catchR * w, 13, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      ctx.font = "64px system-ui";
      ctx.fillText(theme.basket, st.basketX * w, basketY * h);
      st.raf = requestAnimationFrame(loop);
    };
    st_start();
    function st_start() { g.current.raf = requestAnimationFrame(loop); }

    const move = (clientX: number) => {
      const rect = cvs.getBoundingClientRect();
      g.current.basketX = Math.max(0.08, Math.min(0.92, (clientX - rect.left) / rect.width));
    };
    const onPointer = (e: PointerEvent) => { move(e.clientX); };
    cvs.addEventListener("pointermove", onPointer);
    cvs.addEventListener("pointerdown", onPointer);

    return () => {
      cancelAnimationFrame(g.current.raf);
      window.removeEventListener("resize", resize);
      cvs.removeEventListener("pointermove", onPointer);
      cvs.removeEventListener("pointerdown", onPointer);
    };
    // relance la boucle quand on rejoue (status repasse à "play")
  }, [status, finish]);

  const rar = reward?.rarity;
  const rewardCol = rar === "legendary" ? "#ffd43b" : rar === "rare" ? "var(--kid-cyan)" : "var(--kid-text-3)";

  return (
    <div style={S.screen}>
      <div style={S.header}>
        <button style={S.close} onClick={onClose} aria-label="Fermer">✕</button>
        <div style={S.title}>NIVEAU {level} · {score}/{TARGET}</div>
        <div style={S.lives}>{"❤️".repeat(Math.max(0, lives))}</div>
      </div>
      <div style={S.boardWrap}>
        <canvas ref={canvasRef} style={{ width: "100%", height: "100%", touchAction: "none", display: "block" }} />
      </div>
      <div style={S.hint}>Glisse ton doigt n'importe où (garde-le en bas) pour déplacer le panier · évite les 💣</div>

      {status !== "play" && (
        <div style={S.overlay} onClick={start}>
          <div style={S.card}>
            <div style={{ fontSize: 40 }}>{status === "won" ? "🏆" : "💥"}</div>
            <div style={{ color: status === "won" ? "var(--kid-cyan)" : "#ff6b6b", fontWeight: 800, fontSize: 20 }}>
              {status === "won" ? "GAGNÉ !" : "PERDU !"}
            </div>
            {status === "won" && reward && (
              <div style={{ ...S.rewardBox, borderColor: rewardCol }}>
                <div style={{ fontSize: 44 }}>{reward.emoji}</div>
                <div style={{ fontWeight: 700 }}>{reward.name}</div>
                <div style={{ color: rewardCol, fontSize: 12, fontFamily: "var(--kid-font-mono)", letterSpacing: "0.14em" }}>CRÉATURE DÉBLOQUÉE&nbsp;!</div>
              </div>
            )}
            {status === "won" && !reward && <div style={S.sub}>Bien joué&nbsp;! Rejoue pour tenter une créature.</div>}
            {status === "lost" && <div style={S.sub}>Presque&nbsp;! On retente&nbsp;?</div>}
            <button style={S.replay} onClick={(e) => { e.stopPropagation(); start(); }}>🔄 Rejouer</button>
            <button style={S.leave} onClick={(e) => { e.stopPropagation(); onClose(); }}>Quitter</button>
          </div>
        </div>
      )}
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  screen: { position: "fixed", inset: 0, zIndex: 62, background: "var(--kid-bg)", display: "flex", flexDirection: "column", color: "var(--kid-text)", fontFamily: "var(--kid-font-sans, system-ui)" },
  header: { display: "flex", alignItems: "center", gap: 12, padding: "16px 16px 8px", borderBottom: "1px solid var(--kid-border)" },
  close: { width: 40, height: 40, borderRadius: 12, border: "1px solid var(--kid-cyan-40)", background: "transparent", color: "var(--kid-cyan)", fontSize: 18, cursor: "pointer" },
  title: { flex: 1, fontFamily: "var(--kid-font-mono)", letterSpacing: "0.12em", fontSize: 15, color: "var(--kid-cyan)" },
  lives: { fontSize: 15 },
  boardWrap: { flex: 1, position: "relative", overflow: "hidden" },
  hint: { textAlign: "center", color: "var(--kid-text-3)", fontSize: 12, padding: "8px 16px calc(env(safe-area-inset-bottom) + 8px)" },
  overlay: { position: "fixed", inset: 0, zIndex: 64, background: "rgba(3,5,9,0.8)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 },
  card: { textAlign: "center", padding: "26px 30px", borderRadius: 20, border: "1px solid var(--kid-cyan-40)", background: "var(--kid-bg)", maxWidth: 340, width: "100%" },
  sub: { color: "var(--kid-text-3)", fontSize: 13, marginTop: 6 },
  rewardBox: { margin: "16px auto 4px", padding: "14px", borderRadius: 16, border: "2px solid var(--kid-cyan)", display: "flex", flexDirection: "column", gap: 4, alignItems: "center" },
  replay: { display: "block", width: "100%", marginTop: 18, padding: "13px", borderRadius: 14, border: "none", background: "linear-gradient(180deg, var(--kid-cyan), #008cbe)", color: "#04222c", fontSize: 16, fontWeight: 800, cursor: "pointer" },
  leave: { display: "block", width: "100%", marginTop: 10, padding: "11px", borderRadius: 12, border: "1px solid var(--kid-cyan-40)", background: "transparent", color: "var(--kid-text-2)", fontSize: 14, cursor: "pointer" },
};
